import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Extend the i18n type to allow custom properties
declare module 'i18next' {
  interface i18n {
    loadLanguageAsync?: (lng: string) => Promise<void>;
  }
}

// Import English as fallback to prevent loading issues
import en from './en.json';

// Use Vite's glob import for language files (excluding en.json which is statically imported)
const languageModules = import.meta.glob<{ translation: Record<string, string> }>(['./*.json', '!./en.json'], { import: 'default' });

// Create a function to dynamically load languages
const loadLanguage = async (lng: string) => {
  if (lng === 'en') {
    return en.translation;
  }

  try {
    const modulePath = `./${lng}.json`;
    if (languageModules[modulePath]) {
      const languageModule = await languageModules[modulePath]();
      return languageModule.translation;
    }
    console.warn(`Language file ${lng} not found, falling back to English`);
    return en.translation;
  } catch (error) {
    console.warn(`Failed to load language ${lng}, falling back to English`);
    return en.translation;
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en
    },
    fallbackLng: 'en',
    lng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

// Add language loading function to i18n
i18n.loadLanguageAsync = async (lng) => {
  if (!i18n.hasResourceBundle(lng, 'translation')) {
    const translations = await loadLanguage(lng);
    i18n.addResourceBundle(lng, 'translation', translations);
  }
};

// Override changeLanguage to load resources dynamically
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng?: string) => {
  if (lng && i18n.loadLanguageAsync) {
    await i18n.loadLanguageAsync(lng);
  }
  return originalChangeLanguage(lng);
};

export default i18n;